import { SearchResults } from "../../Shared/scriptts/interfaces";
import { addToArrayParam, removeFromArrayParam } from "../search-filters-main";
import { params, executeSearchUrl, displayResults, brandedSearch } from '../search-main';
import { debounce, isEmpty } from "../../Shared/scriptts/utilities";

declare var gIsSearchFiltersEnabled: boolean;
declare var departments: any;
declare var agencies: any;

let showNoCountDepartments = false;
let showNoCountAgencies = false;
let showAllOrganizations = false;
let noCountDepartments: HTMLLIElement[] = [];
let noCountAgencies: HTMLLIElement[] = [];
let foundDepartments: HTMLLIElement[] = [];
let foundAgencies: HTMLLIElement[] = [];
let selectedOrganizations: string[] = [];
let searchResults: SearchResults | null = null;

const countIcon = document.createElement('span');

export function resetOrganizationFilter() {
    selectedOrganizations = [];
    setClearButtonState();
}

/**
 * Display Department and Agency filters
 * @param data The data returned from the search API
 */
export function displayOrganizationFilter(data: SearchResults) {
    const includeOrganizations = document.querySelector<HTMLInputElement>('#include-organizations');
    const excludeOrganizations = document.querySelector<HTMLInputElement>('#exclude-organizations');

    if (excludeOrganizations) {
        excludeOrganizations.checked = params.ExcludeOrganizations === true;
    }
    if (includeOrganizations) {
        includeOrganizations.checked = params.ExcludeOrganizations !== true;
    }

    if (gIsSearchFiltersEnabled) {
        buildOrganizationFilter(data, '');
        return;
    }

    buildDepartmentFilter(data);
    buildAgencyFilter(data);
    const count = params.Agency?.length + params.Department?.length;
    const titleElem = document.querySelector<HTMLElement>('#titleaccordion-organization');
    if (titleElem !== null) {
        countIcon.className = count > 0 ? 'bg-warning rounded-full py-1 px-2 ml-2' : 'bg-warning rounded-full py-1 px-2 ml-2 hidden';
        countIcon.innerText = count.toString();
        if (!titleElem.contains(countIcon)) {
            titleElem.appendChild(countIcon);
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const searchInput = document.querySelector<HTMLInputElement>('#organization-search');
    document.querySelector<HTMLButtonElement>('#apply-filters-organization')?.addEventListener('click', applyButtonClicked);
    searchInput?.addEventListener('keyup', debounce(() => buildOrganizationFilter(null, searchInput?.value ?? ''), 500));
    document.querySelector<HTMLButtonElement>('#clear-filters-organization')?.addEventListener('click', clearButtonClicked);
    document.querySelector<HTMLInputElement>('#show-all-organizations')?.addEventListener('change', showAllOrganizationsChanged);
    document.querySelector<HTMLButtonElement>('#filter-org-dept-toggle')?.addEventListener('click', departmentToggleClicked);
    document.querySelector<HTMLButtonElement>('#filter-org-agency-toggle')?.addEventListener('click', agencyToggleClicked);
    document.querySelector<HTMLButtonElement>('#filter-dept-showall-toggle')?.addEventListener('click', showAllDepartmentToggleClicked);
    document.querySelector<HTMLButtonElement>('#filter-agency-showall-toggle')?.addEventListener('click', showAllAgencyToggleClicked);
    document.querySelector<HTMLInputElement>('#dept-search')?.addEventListener('keyup', deptSearchKeyup);
    document.querySelector<HTMLInputElement>('#agency-search')?.addEventListener('keyup', agencySearchKeyup);
});

function showAllOrganizationsChanged() {
    const showAllOrgsCheck = document.querySelector<HTMLInputElement>('#show-all-organizations');
    if (!showAllOrgsCheck) {
        return;
    }
    showAllOrganizations = showAllOrgsCheck.checked;
    buildOrganizationFilter(null, document.querySelector<HTMLInputElement>('#organization-search')?.value ?? '');
}

function buildOrganizationFilter(data: SearchResults | null = null, searchText: string = '') {
    const orgContainer = document.querySelector<HTMLUListElement>('#filter-organization-list');
    if (!orgContainer) {
        return;
    }
    if (data != null) {
        searchResults = data;
    }
    const searchLower = searchText.trim().toLowerCase();
    const isSearch = searchLower.length > 2;
    const brandedSearchOrg = !brandedSearch ? null : (brandedSearch.Departments || brandedSearch.Agencies);
    const isFilterSelectable = !brandedSearchOrg || brandedSearchOrg.length === 0;

    interface OrgValue { Name: string; Sort: string; Parent: string, Acronym: string | undefined | null }
    const orgs = [
        ...Object.entries(agencies).map(([key, value]) => ({ key, value: value as OrgValue, type: 'Agency' })),
        ...Object.entries(departments).map(([key, value]) => ({ key, value: value as OrgValue, type: 'Department' }))
    ]
        .sort((left, right) => left.value.Name.localeCompare(right.value.Name));
    const listFragment = document.createDocumentFragment();
    const liCss = 'flex items-start';

    let isOrgsFound = false;

    orgContainer.innerHTML = '';

    orgs.forEach(org => {
        const orgLi = document.createElement('li');
        const orgCheckbox = document.createElement('input');
        const orgLabel = document.createElement('label');
        const orgCount = document.createElement('span');
        const checked = selectedOrganizations.includes(org.key) || params[org.type]?.indexOf(org.key) > -1;
        const matchesSearch = isSearch && (startsWithNameWord(org.value.Name, searchLower) || org.value.Acronym?.toLowerCase().includes(searchLower));
        const typeLower = org.type.toLowerCase();
        const parentDiv = document.createElement('div');
        const codeCounts = searchResults?.f?.[org.type === 'Agency' ? 'a' : 'd'];
        const jobCount = codeCounts && org.key in codeCounts ? codeCounts[org.key] : 0;

        orgCheckbox.type = 'checkbox';
        orgCheckbox.name = org.type;
        orgCheckbox.value = org.key;
        orgCheckbox.id = `${typeLower}-id-${org.key}`;
        orgCheckbox.checked = checked;
        orgCheckbox.disabled = !isFilterSelectable;
        orgCheckbox.addEventListener('change', organizationInputChanged);

        orgLabel.setAttribute('for', orgCheckbox.id);
        orgLabel.textContent = org.value.Name;

        orgCount.classList.add('text-gray', 'text-sm', 'ml-1');
        orgCount.textContent = `(${jobCount})`;

        parentDiv.className = 'text-gray';
        parentDiv.textContent = org.type === 'Agency' ? org.value.Parent : org.value.Parent ?? org.value.Name;
        orgLabel.appendChild(orgCount);
        orgLabel.appendChild(parentDiv);

        orgLi.id = `${typeLower}-li-${org.key}`;
        orgLi.className = liCss;
        orgLi.appendChild(orgCheckbox);
        orgLi.appendChild(orgLabel);

        if (jobCount === 0 && !showAllOrganizations && !orgCheckbox.checked) {
            orgLi.classList.add('hidden');
        }
        else {
            orgLi.classList.remove('hidden');
        }
        if (isSearch) {
            if (matchesSearch) {
                orgLi.classList.remove('hidden');
                isOrgsFound = true;
            }
            else {
                if (!orgCheckbox.checked) {
                    orgLi.classList.add('hidden');
                }
                
            }
        }
        listFragment.appendChild(orgLi);

        if (checked && !selectedOrganizations.includes(org.key)) {
            selectedOrganizations.push(org.key);
        }
    });

    if (isSearch) {
        if (!isOrgsFound) {
            document.querySelector<HTMLElement>('#filter-organization-no-results')?.classList.remove('hidden');
        }
        else {
            document.querySelector<HTMLElement>('#filter-organization-no-results')?.classList.add('hidden');
        }
    }
    else {
        document.querySelector<HTMLElement>('#filter-organization-no-results')?.classList.add('hidden');
    }

    orgContainer.appendChild(listFragment);
    setFilterButtonState();
    setClearButtonState();
}

function startsWithNameWord(name: string, searchLower: string): boolean {
    const nameWords = name.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const searchWords = searchLower.split(/[^a-z0-9]+/).filter(Boolean);

    if (searchWords.length === 0) {
        return false;
    }

    let nameWordIndex = 0;

    return searchWords.every(searchWord => {
        const matchingIndex = nameWords.findIndex((nameWord, index) => index >= nameWordIndex && nameWord.startsWith(searchWord));
        if (matchingIndex === -1) {
            return false;
        }

        nameWordIndex = matchingIndex + 1;
        return true;
    });
}

function setClearButtonState() {
    const clearButton = document.querySelector<HTMLButtonElement>('#clear-filters-organization');
    const orgContainer = document.querySelector<HTMLUListElement>('#filter-organization-list');
    if (!clearButton || !orgContainer) {
        return;
    }

    if (orgContainer.querySelector('input[type="checkbox"]:checked') !== null) {
        clearButton.classList.remove('hidden');
    }
    else {
        clearButton.classList.add('hidden');
    }
}

function organizationInputChanged(evt: Event) {
    const orgInput = (<HTMLInputElement>evt.target);

    if (!orgInput.checked) {
        const index = selectedOrganizations.indexOf(orgInput.value);

        if (index > -1) {
            selectedOrganizations.splice(index, 1);
        }
    }
    else if (!selectedOrganizations.includes(orgInput.value)) {
        selectedOrganizations.push(orgInput.value);
    }

    setClearButtonState();
}

function applyButtonClicked() {
    const orgContainer = document.querySelector<HTMLUListElement>('#filter-organization-list');
    if (!orgContainer) {
        return;
    }

    orgContainer.querySelectorAll('input').forEach(input => {
        let type = input.id.substring(0, input.id.indexOf('-'));
        type = type[0].toUpperCase() + type.slice(1);

        if (input.checked) {
            addToArrayParam(type, input.value);
            if (!selectedOrganizations.includes(input.value)) {
                selectedOrganizations.push(input.value);
            }
        }
        else {
            removeFromArrayParam(type, input.value);
            const index = selectedOrganizations.indexOf(input.value);

            if (index > -1) {
                selectedOrganizations.splice(index, 1);
            }
        }
    });

    const excludeOrgs = document.querySelector<HTMLInputElement>('#exclude-organizations');
    params.ExcludeOrganizations = selectedOrganizations.length > 0 ? excludeOrgs?.checked === true : undefined;

    params.Page = '1';
    setFilterButtonState();
    displayResults(executeSearchUrl, false, true);
}

function clearButtonClicked() {
    const searchInput = document.querySelector<HTMLInputElement>('#organization-search');

    params.Agency = [];
    params.Department = [];
    params.ExcludeOrganizations = undefined;

    if (searchInput) {
        searchInput.value = '';
    }

    const includeOrganizations = document.querySelector<HTMLInputElement>('#include-organizations');
    const excludeOrganizations = document.querySelector<HTMLInputElement>('#exclude-organizations');
    if (includeOrganizations) {
        includeOrganizations.checked = true;
    }
    if (excludeOrganizations) {
        excludeOrganizations.checked = false;
    }

    selectedOrganizations = [];
    document.querySelector<HTMLUListElement>('#filter-organization-list')?.querySelectorAll('input').forEach(input => input.checked = false);
    params.Page = '1';
    document.querySelector<HTMLButtonElement>('#clear-filters-organization')?.classList.add('hidden');
    setFilterButtonState();
    displayResults(executeSearchUrl, false, true);
}

function setFilterButtonState() {
    const filterButton = document.querySelector<HTMLButtonElement>('#filter-button-organization');
    const buttonTextElem = document.querySelector<HTMLElement>('#filter-button-text-organization');
    if (!filterButton || !buttonTextElem) {
        return;
    }
    
    if (isEmpty(params.Agency) && isEmpty(params.Department)) {
        buttonTextElem.textContent = 'Agency';
        filterButton.classList.remove('button-search-filter-applied');
        filterButton.classList.add('button-search-filter');
        filterButton.querySelectorAll<HTMLElement>('svg').forEach(icon => {
            icon.classList.remove('fill-white');
            icon.classList.add('fill-gray-darkest');
        });
    }
    else {
        const orgs = [...params.Agency ?? [], ...params.Department ?? []].sort();
        const orgCode = orgs[0];
        const orgName = agencies[orgCode]?.Name ?? departments[orgCode]?.Name ?? orgCode;
        const totalCount = orgs.length;

        buttonTextElem.textContent = `${orgName}${totalCount > 1 ? ` + ${totalCount - 1}` : ''}`;
        filterButton.classList.add('button-search-filter-applied');
        filterButton.classList.remove('button-search-filter');
        filterButton.querySelectorAll<HTMLElement>('svg').forEach(icon => {
            icon.classList.add('fill-white');
            icon.classList.remove('fill-gray-darkest');
        });
    }
}

// todo: Everything below this line can be deleted once the SearchFilters feature toggle is no longer needed.
function buildDepartmentFilter(data: SearchResults, searchText: string = '') {
    const deptContainer = document.querySelector<HTMLUListElement>('#filter-dept-list');
    if (deptContainer === null) {
        return;
    }
    const searchLength = searchText.length;

    deptContainer.innerHTML = '';
    for (let d in departments) {
        let jobCount = !data.f['d'][d] ? "0" : data.f['d'][d].toString();
        const li = document.createElement('li');
        const input = document.createElement('input');
        const label = document.createElement('label');
        const checked = params.Department?.indexOf(d) > -1;

        input.type = "checkbox";
        input.name = "Department";
        input.value = d;
        input.id = `department-id-${d}`;
        input.checked = checked;

        label.setAttribute('for', input.id);
        label.id = `department-label-${d}`;
        label.innerHTML = `${departments[d]?.Name} <span class="text-gray text-sm">(${jobCount})</span>`;

        li.id = `department-li-${d}`;
        li.setAttribute('data-count', jobCount);
        li.appendChild(input);
        li.appendChild(label);

        input.addEventListener('change', departmentChanged);

        if (jobCount === "0" && searchLength < 3) {
            noCountDepartments.push(li);
            
            if (showNoCountDepartments === false && checked === false) {
                li.classList.add('hidden');
            }
        }

        deptContainer.appendChild(li);
    }
}

function buildAgencyFilter(data: SearchResults) {
    const agencyContainer = document.querySelector<HTMLUListElement>('#filter-agency-list');
    if (agencyContainer === null) {
        return;
    }

    agencyContainer.innerHTML = '';
    for (let a in agencies) {
        let jobCount = !data.f['a'][a] ? "0" : data.f['a'][a].toString();
        const li = document.createElement('li');
        const input = document.createElement('input');
        const label = document.createElement('label');

        input.type = "checkbox";
        input.name = "Agency";
        input.value = a;
        input.id = `agency-id-${a}`;
        input.checked = params.Agency?.indexOf(a) > -1;
        input.addEventListener('change', agencyChanged);

        label.setAttribute('for', input.id);
        label.id = `agency-label-${a}`;
        label.innerHTML = `${agencies[a]?.Name} <span class="text-gray text-sm">(${jobCount})</span>`;

        li.id = `agency-li-${a}`;
        li.setAttribute('data-count', jobCount);
        li.appendChild(input);
        li.appendChild(label);

        if (jobCount === "0") {
            noCountAgencies.push(li);

            if (showNoCountAgencies === false) {
                li.classList.add('hidden');
            }
        }

        agencyContainer.appendChild(li);
    }
}

function departmentToggleClicked() {
    document.querySelector<HTMLElement>('#filter-dept')?.classList.remove('hidden');
    document.querySelector<HTMLElement>('#filter-agency')?.classList.add('hidden');
    document.querySelector<HTMLButtonElement>('#filter-org-dept-toggle')?.classList.add('border-b-4');
    document.querySelector<HTMLButtonElement>('#filter-org-agency-toggle')?.classList.remove('border-b-4');
}

function agencyToggleClicked() {
    document.querySelector<HTMLElement>('#filter-dept')?.classList.add('hidden');
    document.querySelector<HTMLElement>('#filter-agency')?.classList.remove('hidden');
    document.querySelector<HTMLButtonElement>('#filter-org-dept-toggle')?.classList.remove('border-b-4');
    document.querySelector<HTMLButtonElement>('#filter-org-agency-toggle')?.classList.add('border-b-4');
}

function departmentChanged(evt: Event) {
    const deptInput = <HTMLInputElement>(evt.target);
    if (params.Department === null) {
        params.Department = [];
    }
    if (deptInput.checked) {
        params.Department.push(deptInput.value);
    }
    else {
        params.Department.splice(params.Department.indexOf(deptInput.value), 1);
    }
    const count = params.Agency?.length + params.Department?.length;
    if (count > 0) {
        countIcon.innerText = count.toString();
        countIcon.classList.remove('hidden');
    }
    else {
        countIcon.innerText = '0';
        countIcon.classList.add('hidden');
    }
    params.Page = '1';
    displayResults(executeSearchUrl, false, true);
}

function agencyChanged(evt: Event) {
    const agencyInput = <HTMLInputElement>(evt.target);
    if (params.Agency === null) {
        params.Agency = [];
    }
    if (agencyInput.checked) {
        params.Agency.push(agencyInput.value);
    }
    else {
        params.Agency.splice(params.Agency.indexOf(agencyInput.value), 1);
    }
    const count = params.Agency?.length + params.Department?.length;
    if (count > 0) {
        countIcon.innerText = count.toString();
        countIcon.classList.remove('hidden');
    }
    else {
        countIcon.innerText = '0';
        countIcon.classList.add('hidden');
    }
    params.Page = '1';
    displayResults(executeSearchUrl, false, true);
}

function setDepartmentFiltersDisplay() {
    const searchLength = document.querySelector<HTMLInputElement>('#dept-search')?.value.length ?? 0;

    document.querySelector<HTMLUListElement>('#filter-dept-list')?.querySelectorAll<HTMLLIElement>('li').forEach(li => {
        const found = foundDepartments.indexOf(li) > -1;
        const inNoCountList = noCountDepartments.indexOf(li) > -1;

        if (li.querySelector<HTMLInputElement>('input')?.checked || (inNoCountList && showNoCountDepartments && (searchLength < 3 || found)) || (!inNoCountList && (searchLength < 3 || found))) {
            li.classList.remove('hidden');
        }
        else {
            li.classList.add('hidden');
        }
    });
}

function setAgencyFiltersDisplay() {
    const searchLength = document.querySelector<HTMLInputElement>('#agency-search')?.value.length ?? 0;

    document.querySelector<HTMLUListElement>('#filter-agency-list')?.querySelectorAll<HTMLLIElement>('li').forEach(li => {
        const found = foundAgencies.indexOf(li) > -1;
        const inNoCountList = noCountAgencies.indexOf(li) > -1;

        if (li.querySelector<HTMLInputElement>('input')?.checked || (inNoCountList && showNoCountAgencies && (searchLength < 3 || found)) || (!inNoCountList && (searchLength < 3 || found))) {
            li.classList.remove('hidden');
        }
        else {
            li.classList.add('hidden');
        }
    });
}

function showAllDepartmentToggleClicked() {
    const toggleButton = document.querySelector<HTMLButtonElement>('#filter-dept-showall-toggle');
    if (toggleButton === null) {
        return;
    }
    showNoCountDepartments = !showNoCountDepartments;
    toggleButton.innerText = showNoCountDepartments ? 'Hide options with 0 jobs' : 'Show options with 0 jobs';
    setDepartmentFiltersDisplay();
}

function showAllAgencyToggleClicked() {
    const toggleButton = document.querySelector<HTMLButtonElement>('#filter-agency-showall-toggle');
    if (toggleButton === null) {
        return;
    }
    showNoCountAgencies = !showNoCountAgencies;
    toggleButton.innerText = showNoCountAgencies ? 'Hide options with 0 jobs' : 'Show options with 0 jobs';
    setAgencyFiltersDisplay();
}

function deptSearchKeyup(evt: Event) {
    const searchInput = <HTMLInputElement>(evt.target);
    foundDepartments = [];
    document.querySelector<HTMLUListElement>('#filter-dept-list')?.querySelectorAll('li').forEach(li => {
        const dept = li.id.substring(li.id.lastIndexOf('-') + 1);
        const deptInput = document.querySelector<HTMLInputElement>(`#department-id-${dept}`);
        if (deptInput === null) {
            return;
        }
        if (searchInput.value.length < 3) {
            document.querySelector<HTMLButtonElement>('#filter-dept-showall-toggle')?.classList.remove('md:hidden');
        }
        else {
            const found = departments[dept]?.Name.toLowerCase().includes(searchInput.value.toLowerCase());
            if (found === true) {
                foundDepartments.push(li);
            }
        }        
    });
    setDepartmentFiltersDisplay();
}

function agencySearchKeyup(evt: Event) {
    const searchInput = <HTMLInputElement>(evt.target);
    foundAgencies = [];
    document.querySelector<HTMLUListElement>('#filter-agency-list')?.querySelectorAll('li').forEach(li => {
        const agency = li.id.substring(li.id.lastIndexOf('-') + 1);
        const agencyInput = document.querySelector<HTMLInputElement>(`#agency-id-${agency}`);
        if (agencyInput === null) {
            return;
        }
        if (searchInput.value.length < 3) {
            document.querySelector<HTMLButtonElement>('#filter-agency-showall-toggle')?.classList.remove('md:hidden');
        }
        else {
            const found = agencies[agency]?.Name.toLowerCase().includes(searchInput.value.toLowerCase());
            if (found === true) {
                foundAgencies.push(li);
            }
        }
    });
    setAgencyFiltersDisplay();
}
